"use client";

import { useEffect, useRef, useSyncExternalStore, type ReactNode } from "react";

// video-kit renders of the whole 3D phone, made by scripts/hero-3d/encode.sh.
// Literal paths so the assets test can see them. A re-render takes a NEW
// filename: /assets/hero-3d is served immutable for a year (infra/scripts/
// deploy.sh), so an overwrite keeps serving the old video.
//
// The `sway` arm, which won the `hero-3d-video` experiment (96268): the phone
// drifts a little sideways, glides back to where it started while the screen
// fades to dark, and the film restarts behind the dark screen. Phones get their
// own cut that stands closer: front-on, larger, leaning back and slowly tilting
// upright.
//
// Two widths. 720 covers the 560px box on a 2x desktop display; `sm` is 480 and
// is what a phone loads. A <source> inside a <video> has no media attribute
// (that is a <picture> feature), so the width is picked here instead.
const CLIP = {
  av1: "/assets/hero-3d/phone-sway-slow.av1.mp4",
  mp4: "/assets/hero-3d/phone-sway-slow.mp4",
  poster: "/assets/hero-3d/phone-sway-slow-start.webp",
  smAv1: "/assets/hero-3d/phone-sway-tilt-sm.av1.mp4",
  smMp4: "/assets/hero-3d/phone-sway-tilt-sm.mp4",
  smPoster: "/assets/hero-3d/phone-sway-tilt-sm-poster.webp",
  still: "/assets/hero-3d/phone-sway-slow-hold.webp",
};

const noopSubscribe = () => () => {};

// Read a media query as a store rather than in an effect, so the first client
// render already knows. A reduced-motion visitor must never mount the <video> at
// all: hiding it in CSS would still download it. The width decides which encode
// to fetch, and getting that wrong on the first render costs a second download.
function useMediaQuery(query: string): boolean {
  return useSyncExternalStore(
    (onChange) => {
      const list = window.matchMedia(query);
      list.addEventListener("change", onChange);
      return () => list.removeEventListener("change", onChange);
    },
    () => window.matchMedia(query).matches,
    () => false, // server render: the control picture, never the video
  );
}

// True once the page's own load event has fired, so the swap never lands
// before the preloaded hero picture has painted (the old experiment waited for
// the PostHog variant, which was well past LCP; hydration alone is not).
function usePageLoaded(): boolean {
  return useSyncExternalStore(
    (onChange) => {
      window.addEventListener("load", onChange, { once: true });
      return () => window.removeEventListener("load", onChange);
    },
    () => document.readyState === "complete",
    () => false,
  );
}

// Data Saver visitors keep the picture: the poster and clip are pure extra bytes.
const saveData = () =>
  (navigator as Navigator & { connection?: { saveData?: boolean } }).connection?.saveData === true;

// The hero's right column. The server render and hydration render `children`
// (the LCP picture), so the indexed page and its preload contract are untouched;
// the video swaps in after the load event and never competes with the picture.
export function HeroMedia({ children, alt }: { children: ReactNode; alt: string }) {
  const loaded = usePageLoaded();
  const lowData = useSyncExternalStore(noopSubscribe, saveData, () => false);
  const reducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  // Exactly Tailwind's `max-sm:` (below 40rem), so the phone cut and its box
  // switch at the same width.
  const small = useMediaQuery("not all and (min-width: 40rem)");
  if (!loaded || lowData) return children;

  return (
    <div
      // The phone fills ~70% of the clip's height, so the box runs wider than the
      // phone should read: at 560px the body lands near the control photo's size.
      // 96vw is wider than the padded column on a phone, where mx-auto cannot
      // centre it; the margin centres it on the viewport instead. The phone cut
      // stands straight, so a taller box there crops only the dark sides of the
      // video and draws the phone ~18% larger; the negative margin pulls the
      // section up over the dark band below the phone.
      //
      // The box's top edge stays put on purpose. What sits above the fold decides
      // the poster's LCP size, and it is only a few percent under the control
      // photo's: move the box up and a late swap reports a new, later LCP.
      // The phone moves up inside the box instead (see PHONE_VIDEO).
      className="relative mx-auto aspect-[9/16] w-[96vw] max-w-[560px] max-sm:-mb-20 max-sm:ml-[calc(50%-48vw)] max-sm:aspect-[1/2.1] max-sm:overflow-hidden lg:-my-10"
    >
      {reducedMotion ? (
        /* eslint-disable-next-line @next/next/no-img-element -- ~24 KB still, no optimizer round-trip */
        <img
          src={CLIP.still}
          alt={alt}
          width={720}
          height={1280}
          className="h-full w-full object-cover"
        />
      ) : (
        <LoopVideo key={String(small)} small={small} alt={alt} />
      )}
    </div>
  );
}

// On phones the video runs taller than its box and starts above it: the
// clipped strip is the empty band over the phone, so the phone sits higher.
const PHONE_VIDEO = "max-sm:-mt-16 max-sm:h-[calc(100%+4rem)]";

// Loads and plays only while the phone is on screen, and pauses when it scrolls
// away: a 12s loop running off screen spends battery on nothing.
//
// No blend mode, mask or filter on the video, deliberately. The hero's ink is
// baked into the frames by the encode script; `mix-blend-mode: lighten` here
// made the browser recomposite the video every frame, and it flickered.
function LoopVideo({ small, alt }: { small: boolean; alt: string }) {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const video = videoRef.current;
    // No observer means no way to tell it is on screen: keep the poster.
    if (!video || !("IntersectionObserver" in window)) return;
    const play = () => {
      video.preload = "auto";
      void video.play()?.catch(() => {});
    };
    const observer = new IntersectionObserver(
      // isIntersecting is true at any sliver, and the first callback fires
      // whatever the ratio, so the threshold alone does not hold 35%.
      ([entry]) => (entry.intersectionRatio >= 0.35 ? play() : video.pause()),
      { threshold: 0.35 },
    );
    // Not a byte of video before the page's own load event: a cached flag can
    // swap the video in during hydration, and it must not compete with the
    // preloaded hero photo that is the page's LCP.
    const start = () => observer.observe(video);
    if (document.readyState === "complete") start();
    else window.addEventListener("load", start, { once: true });
    return () => {
      window.removeEventListener("load", start);
      observer.disconnect();
    };
  }, []);

  return (
    <video
      ref={videoRef}
      muted
      loop
      playsInline
      preload="none"
      // The first frame, so the box shows the phone before anything loads.
      poster={small ? CLIP.smPoster : CLIP.poster}
      aria-label={alt}
      className={`h-full w-full object-cover ${PHONE_VIDEO}`}
    >
      {/* AV1 first: about half the bytes of the H.264 fallback Safari needs. */}
      <source src={small ? CLIP.smAv1 : CLIP.av1} type='video/mp4; codecs="av01.0.05M.08"' />
      <source src={small ? CLIP.smMp4 : CLIP.mp4} type="video/mp4" />
    </video>
  );
}
